import { useState } from 'react';
import { IconClock, IconDownload } from '../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, useAsync, useToast } from '../components/ui';
import { api, downloadFile } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatDuration, TIME_KIND_LABELS } from '../lib/format';

export function TimePage() {
  const { can, user } = useAuth();
  const { notify } = useToast();
  const [filter, setFilter] = useState({ status: '', mine: '', from: '', to: '' });
  const [selection, setSelection] = useState<number[]>([]);
  const [manualModal, setManualModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);

  const entries = useAsync(() => api.get('/api/time', filter), [JSON.stringify(filter)]);
  const running = useAsync(() => api.get('/api/time/running'), []);

  const toggle = (id: number) =>
    setSelection((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));

  /**
   * Freigeben läuft direkt, Ablehnen über ein Fenster mit Begründung.
   *
   * Vorher stand hier `window.prompt(...) ?? undefined`: Unterdrückte der
   * Browser den Dialog – auf Mobilgeräten der Normalfall –, wurde trotzdem
   * abgelehnt, nur eben ohne Begründung. Genau die Angabe, die den Vorgang
   * nachvollziehbar macht, fiel damit still unter den Tisch.
   */
  const decide = async (decision: 'approved' | 'rejected', note?: string) => {
    if (!selection.length) return;
    await api.post('/api/time/approve', { ids: selection, decision, note });
    notify(`${selection.length} Eintrag/Einträge ${decision === 'approved' ? 'freigegeben' : 'abgelehnt'}.`, 'success');
    setSelection([]);
    setRejectModal(false);
    entries.reload();
  };

  const stopRunning = async () => {
    if (!running.data) return;
    await api.post(`/api/time/${running.data.id}/stop`);
    running.reload();
    entries.reload();
    notify('Zeiterfassung beendet.', 'success');
  };

  const startTimer = async () => {
    await api.post('/api/time/start', { kind: 'general' });
    running.reload();
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconClock width={22} height={22} />}
        title="Zeiterfassung"
        subtitle="Auftragsbezogene Arbeitszeiten, separat erfasste Fahrzeiten und allgemeine Arbeitszeit – mit Freigabeworkflow und Export für die Lohnbuchhaltung."
        pill="Zeitwirtschaft"
        tone="success"
        actions={
          <>
            {can('time.create') &&
              (running.data ? (
                <button className="btn danger" onClick={stopRunning}>
                  Laufende Erfassung beenden ({running.data.task_no ?? 'Allgemein'})
                </button>
              ) : (
                <button className="btn" onClick={startTimer}>
                  Zeiterfassung starten
                </button>
              ))}
            {can('time.create') && (
              <button className="btn" onClick={() => setManualModal(true)}>
                Zeit nacherfassen
              </button>
            )}
            {can('time.export') && (
              <button
                className="btn"
                onClick={() =>
                  downloadFile('/api/time/export.csv', 'zeiten.csv', { from: filter.from, to: filter.to }).catch(() =>
                    notify('Export fehlgeschlagen.', 'error'),
                  )
                }
              >
                <IconDownload width={15} height={15} /> Lohnexport
              </button>
            )}
          </>
        }
      />

      {entries.data?.summary && (
        <div className="grid cols-3">
          <div className="kpi">
            <span className="kpi-label">Erfasste Zeit</span>
            <span className="kpi-value">{formatDuration(entries.data.summary.totalMinutes)}</span>
          </div>
          <div className="kpi accent-success">
            <span className="kpi-label">Davon abrechenbar</span>
            <span className="kpi-value">{formatDuration(entries.data.summary.billableMinutes)}</span>
          </div>
          <div className="kpi accent-warning">
            <span className="kpi-label">Offene Freigaben</span>
            <span className="kpi-value">{entries.data.summary.openApprovals}</span>
          </div>
        </div>
      )}

      <Card noBody>
        <div className="card-body tight row" style={{ gap: 10 }}>
          <select value={filter.status} onChange={(event) => setFilter({ ...filter, status: event.target.value })} style={{ width: 190 }}>
            <option value="">Alle Freigabestatus</option>
            <option value="open">Offen</option>
            <option value="approved">Freigegeben</option>
            <option value="rejected">Abgelehnt</option>
          </select>
          <input type="date" value={filter.from} onChange={(event) => setFilter({ ...filter, from: event.target.value })} style={{ width: 165 }} />
          <input type="date" value={filter.to} onChange={(event) => setFilter({ ...filter, to: event.target.value })} style={{ width: 165 }} />
          <button
            className={`btn sm ${filter.mine ? 'active' : ''}`}
            onClick={() => setFilter({ ...filter, mine: filter.mine ? '' : 'true' })}
          >
            Nur meine
          </button>
          <div className="spacer" />
          {can('time.approve') && selection.length > 0 && (
            <>
              <span className="small muted">{selection.length} ausgewählt</span>
              <button className="btn primary sm" onClick={() => decide('approved')}>
                Freigeben
              </button>
              <button className="btn sm" onClick={() => setRejectModal(true)}>
                Ablehnen
              </button>
            </>
          )}
        </div>

        {entries.loading ? (
          <Loading />
        ) : !entries.data?.items?.length ? (
          <EmptyState title="Keine Zeiteinträge gefunden" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  {can('time.approve') && <th style={{ width: 34 }} />}
                  <th>Mitarbeiter</th>
                  <th>Art</th>
                  <th>Auftrag / Objekt</th>
                  <th>Beginn</th>
                  <th>Ende</th>
                  <th className="num">Dauer</th>
                  <th>Nachweis</th>
                  <th>Freigabe</th>
                </tr>
              </thead>
              <tbody>
                {entries.data.items.map((entry: any) => (
                  <tr key={entry.id}>
                    {can('time.approve') && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selection.includes(entry.id)}
                          disabled={entry.approval_status !== 'open'}
                          onChange={() => toggle(entry.id)}
                        />
                      </td>
                    )}
                    <td className="small">
                      {entry.first_name} {entry.last_name}
                      {entry.user_id === user?.id && <span className="subtle"> (Sie)</span>}
                    </td>
                    <td>
                      <Badge variant={entry.kind === 'travel' ? 'info' : entry.kind === 'break' ? '' : 'primary'}>
                        {TIME_KIND_LABELS[entry.kind] ?? entry.kind}
                      </Badge>
                    </td>
                    <td className="small">
                      {entry.task_no ? (
                        <>
                          <span className="mono">{entry.task_no}</span>
                          <div className="subtle truncate" style={{ maxWidth: 220 }}>
                            {entry.task_title}
                          </div>
                        </>
                      ) : (
                        <span className="subtle">{entry.property_name ?? 'Ohne Auftragsbezug'}</span>
                      )}
                    </td>
                    <td className="small">{formatDateTime(entry.started_at)}</td>
                    <td className="small">{entry.ended_at ? formatDateTime(entry.ended_at) : <Badge variant="warning">läuft</Badge>}</td>
                    <td className="num">{formatDuration(entry.minutes)}</td>
                    <td className="small">
                      <PresenceProof entry={entry} />
                    </td>
                    <td>
                      <Badge
                        variant={
                          entry.approval_status === 'approved' ? 'success' : entry.approval_status === 'rejected' ? 'danger' : ''
                        }
                      >
                        {{ open: 'Offen', approved: 'Freigegeben', rejected: 'Abgelehnt' }[entry.approval_status as string]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {manualModal && (
        <ManualEntryModal
          onClose={() => setManualModal(false)}
          onSaved={() => {
            setManualModal(false);
            entries.reload();
            notify('Zeit erfasst.', 'success');
          }}
        />
      )}
      {rejectModal && (
        <RejectTimeModal
          count={selection.length}
          onClose={() => setRejectModal(false)}
          onSubmit={(note) => decide('rejected', note)}
        />
      )}
    </div>
  );
}

function RejectTimeModal({
  count,
  onClose,
  onSubmit,
}: {
  count: number;
  onClose: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <Modal
      title={`${count} Eintrag/Einträge ablehnen`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn danger" disabled={note.trim().length < 3} onClick={() => onSubmit(note)}>
            Ablehnen
          </button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="Begründung"
          required
          hint="Sie erscheint bei der Person, die die Zeit erfasst hat – ohne sie bleibt die Ablehnung unerklärt."
        >
          <textarea rows={3} value={note} autoFocus onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ManualEntryModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const tasks = useAsync(() => api.get('/api/tasks', { open: 'true', limit: 100 }), []);
  const [form, setForm] = useState({
    taskId: '',
    kind: 'work',
    startedAt: '',
    endedAt: '',
    note: '',
    isBillable: true,
  });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title="Zeit nacherfassen"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.startedAt || !form.endedAt}
            onClick={async () => {
              try {
                await api.post('/api/time', {
                  taskId: form.taskId ? Number(form.taskId) : null,
                  kind: form.kind,
                  startedAt: form.startedAt.replace('T', ' ') + ':00',
                  endedAt: form.endedAt.replace('T', ' ') + ':00',
                  note: form.note || undefined,
                  isBillable: form.isBillable,
                });
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Auftrag" hint="Leer lassen für allgemeine Arbeitszeit.">
          <SearchSelect
            value={String(form.taskId ?? '')}
            onChange={(wert) => setForm({ ...form, taskId: wert })}
            options={(tasks.data?.items ?? []).map((task: any) => ({ value: String(task.id), label: `${task.task_no} · ${task.title}` }))}
            placeholder="Ohne Auftragsbezug"
          />
        </Field>
        <Field label="Art">
          <select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })}>
            {Object.entries(TIME_KIND_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-grid">
          <Field label="Beginn" required>
            <input type="datetime-local" value={form.startedAt} onChange={(event) => setForm({ ...form, startedAt: event.target.value })} />
          </Field>
          <Field label="Ende" required>
            <input type="datetime-local" value={form.endedAt} onChange={(event) => setForm({ ...form, endedAt: event.target.value })} />
          </Field>
        </div>
        <Field label="Bemerkung">
          <input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </Field>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.isBillable}
            onChange={(event) => setForm({ ...form, isBillable: event.target.checked })}
          />
          <span>Abrechenbar gegenüber dem Eigentümer/Verwalter</span>
        </label>
      </div>
    </Modal>
  );
}

/**
 * Anwesenheitsnachweis einer Buchung.
 *
 * Gezeigt wird, worauf die Zeit beruht: Ortung im Geofence, gelesene
 * NFC-Marke oder gar nichts. Der Abstand steht dabei, weil „im Geofence“ bei
 * 148 von 150 Metern etwas anderes bedeutet als bei 12 – und die Freigabe
 * genau daran hängt, ob jemand nachfragt.
 */
function PresenceProof({ entry }: { entry: any }) {
  // Der Titel sitzt am umgebenden span: Badge nimmt kein title-Attribut, und
  // der Klartext gehört trotzdem daneben – im Zweifel liest ihn die Freigabe.
  const badge = (method: string | null, distance: number | null, label: string) => {
    const [variant, text, hint] =
      !method || method === 'manual'
        ? (['warning', '–', 'ohne Nachweis erfasst'] as const)
        : method === 'nfc'
          ? (['success', 'NFC', 'NFC-Marke am Objekt gelesen'] as const)
          : (['success', distance != null ? `${distance} m` : 'GPS', `Ortung ${distance ?? '?'} m von der Anschrift`] as const);

    return (
      <span title={`${label === '↓' ? 'Ankunft' : 'Abschluss'}: ${hint}`}>
        <Badge variant={variant}>
          {label} {text}
        </Badge>
      </span>
    );
  };

  return (
    <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
      {badge(entry.start_method, entry.start_distance, '↓')}
      {entry.ended_at && badge(entry.end_method, entry.end_distance, '↑')}
    </span>
  );
}
